import { z } from 'zod';
import { NotificationChannelSchema } from '../customer_api/notification_channel.js';
import { ChatToolSchema } from '../customer_api/notification_preferences.js';

/**
 * Revealed (behavioral) travel preferences — mined from a traveler's CONCLUDED
 * trips, distinct from the DECLARED preferences on `CustomerProfileViewSchema`
 * (mileagePlans etc.). The customer-api read surface
 * (`GET /internal/customers/:id/travel-preferences`) emits this shape; the agents
 * tool and the web-chat profile view both parse it (cross-service wire contract).
 *
 * Lean by design: NO traveler PII (no userId/companyId) — the profile alone.
 */

// Intermediate (not exported) — the documented value domains for the constrained
// signals (departure time-of-day bucket; route as "<origin>-<destination>" IATA).
const DepartureWindowSchema = z.enum(['morning', 'afternoon', 'evening']);
const RouteValueSchema = z.string().regex(/^[A-Z]{3}-[A-Z]{3}$/);

// The seat signals the traveler DECLARES in the first-access onboarding (#3946) — never
// mined. `any` is a real answer ("tanto faz"), not an absence.
const SeatSchema = z.enum(['window', 'aisle', 'any']);
const CabinPositionSchema = z.enum(['front', 'back', 'any']);
// Long-haul (6h+) seat: switch to window, keep the usual seat, or any.
const LongHaulSeatSchema = z.enum(['window', 'keep', 'any']);

// Intermediate (not exported) — one signal + its evidence and provenance.
// 'derived' = mined from trips; 'confirmed' = the traveler confirmed a mined value;
// 'declared' = the traveler stated it with nothing mined behind it (sampleSize 0).
const PreferenceSignalSchema = z.object({
  value: z.string(),
  confidence: z.enum(['low', 'medium', 'high']),
  sampleSize: z.number().int().nonnegative(), // observations that fed this signal
  source: z.enum(['derived', 'confirmed', 'declared']),
});

// Intermediate (not exported).
const FrequentRouteSchema = z.object({
  origin: z.string(),
  destination: z.string(),
  count: z.number().int().positive(),
});

export const TravelerTravelPreferencesSchema = z.object({
  airline: PreferenceSignalSchema.nullable(),
  cabinClass: PreferenceSignalSchema.nullable(),
  departureWindow: PreferenceSignalSchema.extend({ value: DepartureWindowSchema }).nullable(),
  hotelChain: PreferenceSignalSchema.nullable(),
  homeBase: PreferenceSignalSchema.nullable(), // most-common origin
  frequentRoutes: z.array(FrequentRouteSchema),
  // Declared-only seat signals (#3946). `.nullish()`: an upstream that predates them
  // omits the keys, and absent reads as "not declared" — mixed deploys keep parsing.
  seat: PreferenceSignalSchema.extend({ value: SeatSchema }).nullish(),
  cabinPosition: PreferenceSignalSchema.extend({ value: CabinPositionSchema }).nullish(),
  longHaulSeat: PreferenceSignalSchema.extend({ value: LongHaulSeatSchema }).nullish(),
});
export type TravelerTravelPreferences = z.infer<typeof TravelerTravelPreferencesSchema>;

const DecisionVerdictSchema = z.enum(['confirmed', 'dismissed']);

// Confirm/dismiss write body (POST .../decisions). Discriminated on `signal` so
// the value domain is enforced per signal: `route` is "<origin>-<destination>"
// IATA, `departureWindow` is a time-of-day bucket, the seat signals their own enums,
// and the other scalar signals take any non-empty string.
export const TravelPreferenceDecisionSchema = z.discriminatedUnion('signal', [
  z.object({
    signal: z.literal('route'),
    value: RouteValueSchema,
    decision: DecisionVerdictSchema,
  }),
  z.object({
    signal: z.literal('departureWindow'),
    value: DepartureWindowSchema,
    decision: DecisionVerdictSchema,
  }),
  z.object({
    signal: z.enum(['airline', 'cabinClass', 'hotelChain', 'homeBase']),
    value: z.string().min(1),
    decision: DecisionVerdictSchema,
  }),
  z.object({ signal: z.literal('seat'), value: SeatSchema, decision: DecisionVerdictSchema }),
  z.object({
    signal: z.literal('cabinPosition'),
    value: CabinPositionSchema,
    decision: DecisionVerdictSchema,
  }),
  z.object({
    signal: z.literal('longHaulSeat'),
    value: LongHaulSeatSchema,
    decision: DecisionVerdictSchema,
  }),
]);
export type TravelPreferenceDecision = z.infer<typeof TravelPreferenceDecisionSchema>;

// The signal keys a traveler can confirm/dismiss — the discriminators of the
// decision union. Match the field names on `TravelerTravelPreferencesSchema`
// exactly; `route` targets one `frequentRoutes` entry.
export type TravelPreferenceSignalKey = TravelPreferenceDecision['signal'];

export const LOCALES = ['pt-BR', 'en', 'es'] as const;
export const LocaleSchema = z.enum(LOCALES);
export type Locale = z.infer<typeof LocaleSchema>;

/** GET /travel-preferences response wrapper — the lean profile (for the agent tool)
 *  plus the onboarding gate fields (for web-chat). */
export const TravelerTravelPreferencesViewSchema = z.object({
  preferences: TravelerTravelPreferencesSchema,
  confirmed: z.boolean(),
  locale: LocaleSchema.nullable(),
  // The company default language (`business.clients.language`), or null when the
  // company has none or names one we do not speak. Kept apart from `locale` on
  // purpose: `locale` is the traveler's OWN choice, which web-chat declares as
  // `uiLocale` (#3539); this one is only a fallback step of the UI cascade (#5579).
  // `.default(null)`: same mixed-deploy reason as `notificationPhone` below.
  companyLocale: LocaleSchema.nullable().default(null),
  // The OTP-verified WhatsApp number persisted at confirm (normalized e164, no
  // plus), or null. Round-trips the "Avisos" row of the preferences wizard —
  // without it the saved phone could never be displayed back. `.default(null)`
  // keeps parses of not-yet-upgraded upstreams working during mixed deploys.
  notificationPhone: z.string().nullable().default(null),
  concludedTripCount: z.number().int().nonnegative(),
  // Chamada única dos programas de fidelidade (customer-api: loyaltyPromptedAt != null).
  // `.default(true)`: upstream que ainda não conhece o campo lê como "já mostrada" —
  // um deploy misto nunca exibe uma chamada que não consegue carimbar.
  loyaltyPrompted: z.boolean().default(true),
  // Banner "Multiplique seus ganhos" da aba Fidelidade do modal de preferências
  // (customer-api: loyaltyBannerDismissedAt != null). Mesmo `.default(true)` e mesmo
  // motivo do campo acima: um deploy misto nunca mostra um banner cujo fechar não
  // consegue carimbar.
  loyaltyBannerDismissed: z.boolean().default(true),
  // Chats da empresa cuja chamada única já foi apresentada, na ordem slack, teams
  // (customer-api: chatIntroducedAt.<tool> != null). `.default(['slack', 'teams'])`:
  // upstream que ainda não conhece o campo lê como "tudo apresentado" — mesmo molde do
  // `loyaltyPrompted`, um deploy misto nunca abre uma chamada que não consegue carimbar.
  chatIntroduced: z.array(ChatToolSchema).default(['slack', 'teams']),
  // Modal de boas-vindas ao VOLL Intelligence (#6292), uma vez por pessoa
  // (customer-api: welcomedAt != null). Só `false` abre a modal: ausente vale como "já
  // recebida", então um upstream que ainda não conhece o campo (deploy misto) nunca abre
  // uma modal que não consegue carimbar — a mesma garantia do `.default(true)` do
  // `loyaltyPrompted`, sem exigir o campo em toda view montada à mão (fixtures, stories).
  welcomed: z.boolean().optional(),
});
export type TravelerTravelPreferencesView = z.infer<typeof TravelerTravelPreferencesViewSchema>;

// The traveler's captured WhatsApp number. Lenient by design — a free-typed phone
// with country code/punctuation; NOT hard-regexed, so a slightly-off format never
// 500s the caller (normalization to E.164 happens server-side). Shared by the
// confirm body and the phone-verification start body.
const PhoneNumberSchema = z.string().trim().min(8).max(24);

/** POST /travel-preferences/confirm body. */
export const TravelPreferenceConfirmSchema = z.object({
  locale: LocaleSchema.optional(),
  notificationChannel: NotificationChannelSchema.optional(),
  notificationPhone: PhoneNumberSchema.optional(),
  // Só o confirm FINAL do onboarding (que tem o passo de fidelidade) manda; as
  // Preferências reusam esta rota para idioma/telefone e não podem carimbar.
  loyaltyPrompted: z.literal(true).optional(),
  // Só o confirm FINAL do onboarding manda, e só quando o passo "Avisos" mostrou aquele
  // chat (a pessoa já o viu ali, então a chamada única dele não volta). As Preferências
  // reusam esta rota para idioma/telefone e nunca mandam.
  chatIntroduced: ChatToolSchema.optional(),
});
export type TravelPreferenceConfirm = z.infer<typeof TravelPreferenceConfirmSchema>;

/** POST /travel-preferences/chat-introduced body — o chat da empresa cuja chamada única
 *  a pessoa acabou de ver (Agora não / Concluir). A pessoa vem da sessão, nunca do corpo. */
export const ChatIntroducedSchema = z.object({ tool: ChatToolSchema });
export type ChatIntroduced = z.infer<typeof ChatIntroducedSchema>;

/** POST /travel-preferences/phone/verify-start body — the number to send a code to. */
export const PhoneVerifyStartSchema = z.object({
  phone: PhoneNumberSchema,
});
export type PhoneVerifyStart = z.infer<typeof PhoneVerifyStartSchema>;

/** Result of verify-start. `not_whatsapp` = Meta rejected the recipient (the
 *  number has no WhatsApp); `send_failed` = any other delivery failure;
 *  `incomplete_number` = a Brazilian number without the mobile 9th digit — nothing
 *  is sent, because completing it would target a number nobody typed (#5122). */
export const PhoneVerifyStartResultSchema = z.object({
  status: z.enum(['sent', 'not_whatsapp', 'send_failed', 'incomplete_number']),
});
export type PhoneVerifyStartResult = z.infer<typeof PhoneVerifyStartResultSchema>;

/** POST /travel-preferences/phone/verify-check body — the 6-digit code the
 *  traveler received on WhatsApp. */
export const PhoneVerifyCheckSchema = z.object({
  code: z.string().regex(/^\d{6}$/),
});
export type PhoneVerifyCheck = z.infer<typeof PhoneVerifyCheckSchema>;

/** Result of verify-check. Only `verified` saves the number (as `notificationPhone`, #6610). */
export const PhoneVerifyCheckResultSchema = z.object({
  status: z.enum(['verified', 'invalid_code', 'expired', 'too_many_attempts']),
});
export type PhoneVerifyCheckResult = z.infer<typeof PhoneVerifyCheckResultSchema>;
